import React from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
} from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import Layout from './components/Layout';
import RoleProtectedRoute from './components/RoleProtectedRoute';

import AuditLogsPage from './pages/AuditLogsPage';
import DashboardPage from './pages/DashboardPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import GroupDetailPage from './pages/GroupDetailPage';
import GroupsPage from './pages/GroupsPage';
import LoginPage from './pages/LoginPage';
import LogsPage from './pages/LogsPage';
import RegisterPage from './pages/RegisterPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SiteDetailPage from './pages/SiteDetailPage';
import SitesControlPage from './pages/SitesControlPage';
import SitesPage from './pages/SitesPage';
import BrandingPage from './pages/BrandingPage';
import OrganisationPage from './pages/OrganisationPage';
import UsagePage from './pages/UsagePage';
import UserDetailPage from './pages/UserDetailPage';
import UsersPage from './pages/UsersPage';
import HelpManualPage from './pages/HelpManualPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AuthRoot() {
  return (
    <ProtectedRoute>
      <Layout />
    </ProtectedRoute>
  );
}

export const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      <Route path="/" element={<AuthRoot />}>
        <Route index element={<DashboardPage />} />
        <Route path="sites" element={<SitesPage />} />
        <Route path="sites/control" element={<SitesControlPage />} />
        <Route path="sites/:id" element={<SiteDetailPage />} />
        <Route path="help/:slug" element={<HelpManualPage />} />
        <Route
          path="users"
          element={
            <RoleProtectedRoute allowedRoles={['super_user', 'site_admin']}>
              <UsersPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="users/:id"
          element={
            <RoleProtectedRoute allowedRoles={['super_user', 'site_admin']}>
              <UserDetailPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="groups"
          element={
            <RoleProtectedRoute allowedRoles={['super_user', 'site_admin']}>
              <GroupsPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="groups/:id"
          element={
            <RoleProtectedRoute allowedRoles={['super_user', 'site_admin']}>
              <GroupDetailPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="organisation"
          element={
            <RoleProtectedRoute allowedRoles={['super_user']}>
              <OrganisationPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="branding"
          element={
            <RoleProtectedRoute allowedRoles={['super_user']}>
              <BrandingPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="audit"
          element={
            <RoleProtectedRoute allowedRoles={['super_user']}>
              <AuditLogsPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="logs"
          element={
            <RoleProtectedRoute allowedRoles={['super_user']}>
              <LogsPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="usage"
          element={
            <RoleProtectedRoute allowedRoles={['super_user', 'site_admin', 'end_user']}>
              <UsagePage />
            </RoleProtectedRoute>
          }
        />
      </Route>
    </>
  ),
  {
    future: {
      v7_relativeSplatPath: true,
    },
  }
);

